import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded'
import AddIcon from '@mui/icons-material/Add'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BusinessSharpIcon from '@mui/icons-material/BusinessSharp'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import GroupsIcon from '@mui/icons-material/Groups'
import HomeIcon from '@mui/icons-material/Home'
import InventoryIcon from '@mui/icons-material/Inventory'
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded'
import LegendToggleIcon from '@mui/icons-material/LegendToggle'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import ManageSearchSharpIcon from '@mui/icons-material/ManageSearchSharp'
import MenuIcon from '@mui/icons-material/Menu'
import NotificationAddIcon from '@mui/icons-material/NotificationAdd'
import PeopleIcon from '@mui/icons-material/People'
import PsychologyIcon from '@mui/icons-material/Psychology'
import SettingsIcon from '@mui/icons-material/Settings'
import StarIcon from '@mui/icons-material/Star'
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {cyan, orange} from '@mui/material/colors'
import grey from '@mui/material/colors/grey'
import {styled, useTheme} from '@mui/material/styles'
import DividerWithText from 'app/components/DividerWithText'
import AwardIcon from 'app/components/icons/AwardIcon'
import {useAppNavigate} from 'app/hooks/navigation/useAppNavigate'
import {LOCAL_CURRENT_TENANT_ID, LOCAL_CURRENT_TENANT_NAME, useAuthProvider} from 'app/modules/auth'
import {logout} from 'app/modules/cognito'
import * as React from 'react'
import {useSelector} from 'react-redux'
import {Link as RouterLink} from 'react-router-dom'
import {selectSavedSearchSlice} from 'redux/selectors/savedSearchSelector'
import {selectLastProjectId} from 'redux/selectors/settingsSelector'

import {useStylesCommon} from 'styles/useStyles'
import {emGetActiveTenantData, emGetTenants} from 'utilities/emUserFeatures'
import {emToAbsoluteUrl} from 'utilities/extensionMethods'

const drawerWidth = 240

const Main = styled('main', {shouldForwardProp: (prop) => prop !== 'open'})<{
  open?: boolean
}>(({theme, open}) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  backgroundColor: '#1a1a26',
}))

const DrawerHeader = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  backgroundColor: '#1a1a26',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}))

interface NavLinks {
  to: string
  icon: React.ReactNode
  title: string
}

const NavBox = styled('div')({
  backgroundColor: '#083866',
  color: 'white',
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  justifyContent: 'space-between',
})
const StyledList = styled(List)({
  backgroundColor: '#083866',
  color: 'white',
  display: 'flex',
  flex: '1 1 auto',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  '&& .MuiListItemIcon-root': {minWidth: '32px'},
  //selected and (selected + hover) states
  '&& .Mui-selected, && .Mui-selected:hover': {
    backgroundColor: 'grey',
    '&, & .MuiListItemIcon-root': {
      color: 'white',
    },
  },
  // hover states
  '& .MuiListItemButton-root:hover': {
    backgroundColor: 'grey',
    '&, & .MuiListItemIcon-root': {
      color: 'white',
    },
  },
})

export default function NavigationDrawerLeft({children}: {children: React.ReactNode}) {
  const navigate = useAppNavigate()
  const {reduxUser} = useAuthProvider()
  const saveSearchRedux = useSelector(selectSavedSearchSlice)
  const lastViewedProjectId = useSelector(selectLastProjectId)
  const handleLogOut = () => {
    // Do NOT do a window.assign call after this. The logout() call redirects the user; adding
    // a window.assign within this call will break the logout.
    logout()
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const discoverylinks: NavLinks[] = [
    {to: '/discovery/new', icon: <AddIcon sx={{color: 'white'}} />, title: 'New Search'},
    {
      to: '/discovery/list',
      icon: <LegendToggleIcon sx={{color: 'white'}} />,
      title: 'Recent Searches',
    },
    {
      to: '/directories/all',
      icon: <InventoryIcon sx={{color: 'white'}} />,
      title: 'Directory Search',
    },
    {
      to: '/notification/searches',
      icon: <NotificationAddIcon sx={{color: 'white'}} />,
      title: 'Notification Searches',
    },
  ]

  const directorylinks: NavLinks[] = [
    {
      to: '/directories/all',
      icon: <InventoryIcon sx={{color: 'white'}} />,
      title: 'Business Directory',
    },
    {
      to: '/businesses/all',
      icon: <BusinessSharpIcon sx={{color: 'white'}} />,
      title: 'Business Directory',
    },
    {
      to: '/concepts/all',
      icon: <PsychologyIcon sx={{color: 'white'}} />,
      title: 'Concept Directory',
    },
    //from '@mui/icons-material/WbIncandescent'
    {
      to: '/people/all',
      icon: <PeopleIcon sx={{color: 'white'}} />,
      title: 'People Directory',
    },
    {
      to: '/awards/all',
      icon: <AwardIcon width='400' height='400' fs='medium' color={'white'} />,
      title: 'Award Directory',
    },
    {
      to: '/innovations/all',
      icon: <LightbulbIcon sx={{color: 'white'}} />,
      title: 'Innovation Directory',
    },
  ]

  const projectlinks: NavLinks[] = [
    {
      to: '/projects/list',
      icon: <ManageSearchSharpIcon sx={{color: 'white'}} />,
      title: 'Projects',
    },
  ]

  const theme = useTheme()
  const [open, setOpen] = React.useState(true)

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }
  const [selectedDashboard, setSelectedDashboard] = React.useState<number | undefined>(undefined)
  const [selectedIndexDiscovery, setSelectedIndexDiscovery] = React.useState<number | undefined>(
    undefined
  )
  const [selectedIndexDirectory, setSelectedIndexDirectory] = React.useState<number | undefined>(
    undefined
  )
  const [selectedIndexProject, setSelectedIndexProject] = React.useState<number | undefined>(
    undefined
  )

  //Setup switch case so didn't have to track the actual count of items, but that makes a selecteditem state for each list, might redo later
  const handleNav = (idx: number, type: string, navigateTo?: string) => {
    switch (type) {
      case 'directory':
        setSelectedIndexDirectory(idx)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        setSelectedDashboard(undefined)
        navigateTo && navigate(navigateTo)
        break
      case 'discovery':
        setSelectedIndexDiscovery(idx)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexProject(undefined)
        setSelectedDashboard(undefined)
        navigateTo && navigate(navigateTo)
        break
      case 'project':
        setSelectedIndexProject(idx)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedDashboard(undefined)
        navigateTo && navigate(navigateTo)
        break
      case 'account':
        setSelectedDashboard(-1)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        navigateTo && navigate(navigateTo)
        handleClose()
        break
      case 'home':
        setSelectedDashboard(-1)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        navigateTo && navigate(navigateTo)
        handleClose()
        break
      case 'homemock':
        setSelectedDashboard(-1)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        navigateTo && navigate(navigateTo)
        handleClose()
        break
      case 'personprofile':
        setSelectedDashboard(undefined)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        handleClose()
        navigateTo && navigate(navigateTo)
        break
      case 'favorites':
        setSelectedDashboard(idx)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        navigateTo && navigate(navigateTo)
        handleClose()
        break
      case 'businessprofile':
        setSelectedDashboard(undefined)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        handleClose()
        navigateTo && navigate(navigateTo)
        break
      case 'admin':
        setSelectedDashboard(undefined)
        setSelectedIndexDirectory(undefined)
        setSelectedIndexDiscovery(undefined)
        setSelectedIndexProject(undefined)
        handleClose()
        navigateTo && navigate(navigateTo)
        break
    }
  }

  const handleTenantChange = (event: any) => {
    localStorage.setItem(LOCAL_CURRENT_TENANT_ID, event.target.value)
    const tenantName = reduxUser
      ? emGetTenants(reduxUser)?.find((f) => f.id === event.target.value)?.name ?? ''
      : ''
    localStorage.setItem(LOCAL_CURRENT_TENANT_NAME, tenantName)
    window.location.reload()
  }
  const classes = useStylesCommon()
  //third coast federal tennt id
  //adc9a26d-41c5-438a-bb4e-0cd941a4153f
  const showAdminLink = reduxUser?.is_global_admin

  const memoSelectedTenant = React.useMemo(() => {
    return (
      <Box className={classes.styledSelect}>
        {/* custom select for this style in appbar */}
        <label className={classes.styledSelectLabel}>Selected Tenant</label>
        <select
          className={classes.styledSelectText}
          onChange={(e) => handleTenantChange(e)}
          value={reduxUser ? emGetActiveTenantData(reduxUser)?.id ?? '' : ''}
        >
          {reduxUser &&
            emGetTenants(reduxUser)?.map((tenant, i) => {
              return (
                <option key={`item${i}`} value={tenant.id}>
                  {tenant.name}
                </option>
              )
            })}
        </select>
        <span className={classes.styledSelectHighlight}></span>
        <span className={classes.styledSelectBar}></span>
      </Box>
    )
  }, [reduxUser])

  const [anchorElSelEco, setAnchorElSelEco] = React.useState<null | HTMLElement>(null)
  const handleCloseEcoMenu = () => {
    setAnchorElSelEco(null)
  }
  const handleClickEcoMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElSelEco(event.currentTarget)
  }
  const memoSelectedEcosystem = React.useMemo(() => {
    const open = Boolean(anchorElSelEco)
    return (
      <div>
        <Box display={'flex'} alignItems={'center'} color={orange[200]}>
          <Button onClick={handleClickEcoMenu} variant={'text'}>
            <Box sx={{p: '1px', bgcolor: cyan['A400'], borderRadius: '50%'}} color={'inherit'}>
              <IconButton>
                <GroupsIcon sx={{color: grey[900]}} fontSize='small' />
              </IconButton>
            </Box>

            <Typography variant={'h6'} color={grey[50]} ml={1}>
              Ecosystems
            </Typography>
          </Button>
        </Box>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          PaperProps={{
            style: {
              width: '250px',
              marginTop: '2px',
              marginLeft: '6px',
              borderTopLeftRadius: '1px',
              borderTopRightRadius: '25px',
            },
          }}
        >
          <MenuItem onClick={handleCloseEcoMenu}>
            <Link href={'/ecosystem'}>Cinergy Ecosystem</Link>
          </MenuItem>
        </Menu>
      </div>
    )
  }, [reduxUser])

  const memoLastSavedSearchIcon = React.useMemo(() => {
    return saveSearchRedux?.savedSearch ? (
      <Box color={grey[50]}>
        <Tooltip title={<Typography>Last viewed Search</Typography>}>
          <IconButton
            onClick={() => navigate(`/discovery/${saveSearchRedux?.savedSearch?.id}/results`)}
            sx={{color: grey[50]}}
            disableRipple
          >
            <LegendToggleIcon fontSize={'large'} />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      <></>
    )
  }, [saveSearchRedux?.savedSearch])

  const memoLastProjectIcon = React.useMemo(() => {
    return lastViewedProjectId ? (
      <Box color={grey[50]}>
        <Tooltip title={<Typography>Last viewed project</Typography>}>
          <IconButton
            onClick={() => navigate(`/projects/detail?pid=${lastViewedProjectId}`)}
            sx={{color: grey[50]}}
            disableRipple
          >
            <AssignmentIcon fontSize={'large'} />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      <></>
    )
  }, [lastViewedProjectId])

  return (
    <Box sx={{display: 'flex'}}>
      <CssBaseline />
      <AppBar position='fixed' open={open}>
        <Toolbar sx={{display: 'flex', justifyContent: 'space-between'}}>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={handleDrawerOpen}
            edge='start'
            sx={{mr: 2, ...(open && {display: 'none'})}}
          >
            <MenuIcon />
          </IconButton>
          {/* need empty box to push over menu and tenant selection, until ecosyem goes bak in */}
          <Box display={'flex'} flexGrow={1}></Box>
          {/* {open && (
            <Box display={'flex'} flexGrow={1}>
              {' '}
              {memoSelectedEcosystem}
            </Box>
          )} */}
          {/* {
            <Link target={'_self'} href={`https://basic-w93jjbfa.bettermode.io/`}>
              <Box display={'flex'} alignItems={'center'} color={orange[200]} mr={4}>
                <Box sx={{p: '1px', bgcolor: grey[50], borderRadius: '50%'}} color={'inherit'}>
                  <IconButton>
                    <GroupsIcon sx={{color: grey[900]}} fontSize='small' />
                  </IconButton>
                </Box>
                <Typography variant={'h6'} color={grey[50]} ml={1}>
                  iScout Community
                </Typography>
              </Box>
            </Link>
          } */}
          {reduxUser && (
            <Box display={'flex'} gap={1}>
              {memoLastSavedSearchIcon}
              {memoLastProjectIcon}

              {memoSelectedTenant}

              <IconButton
                size='small'
                aria-label='account of current user'
                aria-controls='menu-appbar'
                aria-haspopup='true'
                onClick={handleMenu}
                color='inherit'
                disableRipple
              >
                <Box
                  display={'flex'}
                  ml={1}
                  mr={1}
                  gap={1}
                  flexDirection={'row'}
                  justifyContent={'center'}
                  alignContent={'center'}
                  justifyItems={'center'}
                  alignItems={'center'}
                  flexGrow={0}
                >
                  <AccountCircleRoundedIcon fontSize={'large'} />

                  <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant={'body2'} color={'GrayText'}>
                      {reduxUser.email}
                    </Typography>
                    <Box display={'flex'} gap={0.5}>
                      <Typography variant={'body2'} color={'GrayText'}>
                        {reduxUser.first_name}
                      </Typography>
                      <Typography variant={'body2'} color={'GrayText'}>
                        {reduxUser.last_name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </IconButton>
              <Menu
                id='menu-appbar'
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                sx={{
                  mt: 2,
                  '&& .MuiPaper-root': {
                    backgroundColor: 'white',
                    color: grey[900],
                    '&, & .MuiListItemIcon-root': {
                      color: 'grey',
                      pr: 3,
                    },
                  },
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => handleNav(-1, 'home', '/dashboard/home')}>
                  <Box display={'flex'} gap={1}>
                    <HomeIcon />
                    <span style={{color: 'inherit'}}>
                      <Typography variant={'body1'}>Home</Typography>
                    </span>
                  </Box>
                </MenuItem>
                {/* <MenuItem onClick={() => handleNav(-1, 'homemock', '/dashboard/mock')}>
                  <Box display={'flex'} gap={1}>
                    <HomeIcon />
                    <span style={{color: 'inherit'}}>
                      <Typography variant={'body1'}>Mockup Home</Typography>
                    </span>
                  </Box>
                </MenuItem> */}
                {/* <MenuItem onClick={() => handleNav(-1, 'personprofile', '/profile/person')}>
                  <Box display={'flex'} gap={1}>
                    <AccountBoxRoundedIcon />
                    <span style={{color: 'inherit'}}>
                      <Typography variant={'body1'}>My Profile</Typography>
                    </span>
                  </Box>
                </MenuItem> */}
                {/* <MenuItem onClick={() => handleNav(-1, 'businessprofile', '/profile/business')}>
                  <Box display={'flex'} gap={1}>
                    <AccountBoxRoundedIcon />
                    <span style={{color: 'inherit'}}>
                      <Typography variant={'body1'}>Business Profile</Typography>
                    </span>
                  </Box>
                </MenuItem> */}
                <MenuItem onClick={() => handleNav(-1, 'account', '/account')}>
                  <Box display={'flex'} gap={1}>
                    <SettingsIcon />
                    <Typography variant={'body1'}>Settings</Typography>
                  </Box>
                </MenuItem>
                <Divider sx={{color: grey[900]}} />
                {showAdminLink && (
                  <MenuItem onClick={() => handleNav(-1, 'admin', '/admin')}>
                    <Box display={'flex'} gap={1}>
                      <AdminPanelSettingsIcon />

                      <span style={{color: 'inherit'}}>
                        <Typography variant={'body1'}>Admin</Typography>
                      </span>
                    </Box>
                  </MenuItem>
                )}
                <MenuItem>
                  <Box display={'flex'} gap={1} alignItems={'center'}>
                    <LogoutRoundedIcon />

                    <Button
                      variant={'contained'}
                      color={'primary'}
                      onClick={() => handleLogOut()}
                      disableRipple
                      disableFocusRipple
                      disableElevation
                      disableTouchRipple
                    >
                      <Typography variant={'body1'}>Sign Out</Typography>
                    </Button>
                  </Box>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
        variant='persistent'
        anchor='left'
        open={open}
      >
        <DrawerHeader>
          <RouterLink to='/'>
            <img
              style={{maxWidth: '175px', height: 'auto', padding: 0, margin: 0}}
              alt='Logo'
              src={emToAbsoluteUrl('/static/logos/iScoutHorizontalWhite.png')}
            />
          </RouterLink>
          <IconButton color={'primary'} onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? (
              <KeyboardDoubleArrowLeftRoundedIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <NavBox>
          <Box>
            <StyledList>
              <ListItem>
                <ListItemButton
                  selected={selectedDashboard === -1}
                  onClick={() => handleNav(-1, 'home', '/dashboard/home')}
                >
                  <ListItemIcon>
                    <HomeIcon sx={{color: 'white'}} />
                  </ListItemIcon>
                  <ListItemText primary={'Home'} />
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton
                  selected={selectedDashboard === 1}
                  onClick={() => handleNav(1, 'favorites', '/profile/favorites')}
                >
                  <ListItemIcon>
                    <StarIcon sx={{color: 'white'}} />
                  </ListItemIcon>
                  <ListItemText primary={'Favorites'} />
                </ListItemButton>
              </ListItem>
              {/* <ListItem>
                <ListItemButton
                  selected={selectedDashboard === -1}
                  onClick={() => handleNav(-1, 'homemock', '/dashboard/mock')}
                >
                  <ListItemIcon>
                    <HomeIcon sx={{color: 'white'}} />
                  </ListItemIcon>
                  <ListItemText primary={'Home Mockup'} />
                </ListItemButton>
              </ListItem> */}
              <ListItem key={'directories'}>
                <ListItemButton
                  selected={selectedIndexDirectory === 0}
                  onClick={() => handleNav(0, 'directory', directorylinks[0].to)}
                >
                  <ListItemIcon>{directorylinks[0].icon}</ListItemIcon>
                  <ListItemText primary={'Search Directories'} />
                </ListItemButton>
              </ListItem>
              <DividerWithText>Search</DividerWithText>
              {discoverylinks.map((item, index) => (
                <ListItem key={`${item.title}-${index}`}>
                  <ListItemButton
                    selected={selectedIndexDiscovery === index}
                    onClick={() => handleNav(index, 'discovery', item.to)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItemButton>
                </ListItem>
              ))}
              {/* <DividerWithText>Directories</DividerWithText> */}
              {/* {directorylinks.map((item, index) => ( */}
              {/* <ListItem key={'directories'}>
                <ListItemButton
                  selected={selectedIndexDirectory === 0}
                  onClick={() => handleNav(0, 'directory', directorylinks[0].to)}
                >
                  <ListItemIcon>{directorylinks[0].icon}</ListItemIcon>
                  <ListItemText primary={'Directory Search'} />
                </ListItemButton>
              </ListItem> */}

              <DividerWithText>Projects</DividerWithText>
              {projectlinks.map((item, index) => (
                <ListItem key={`${item.title}-${index}`}>
                  <ListItemButton
                    selected={selectedIndexProject === index}
                    onClick={() => handleNav(index, 'project', item.to)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItemButton>
                </ListItem>
              ))}
            </StyledList>
          </Box>

          <Box display={'flex'} flexDirection={'column'} gap={1} m={2}>
            <Box display={'flex'} justifyContent={'center'}>
              <Link
                target='_blank'
                href={'https://www.thirdcoastfederal.com/contact'}
                title='Check out the complete documentation with over 100 components'
                rel='noreferrer'
                sx={{textDecoration: 'none'}}
              >
                <Typography variant={'h6'} color={grey[400]}>
                  Help & Support
                </Typography>
              </Link>
            </Box>
          </Box>
        </NavBox>
      </Drawer>
      <Main open={open}>{children}</Main>
    </Box>
  )
}
